/**
 * Shared i18n helpers - multi-locale strings (et/en) across the platform.
 *
 * `LocalizedString` is either a plain string or a per-locale object
 * (for example `{ et: "Avaleht", en: "Home" }`). Same pattern as aishop's
 * ModuleManifest.name.
 */

export const SUPPORTED_LOCALES = ["et", "en"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "et";

/** Either a plain string or a per-locale map. */
export type LocalizedString = string | Partial<Record<string, string>>;

/**
 * Pick the value for a locale. Fallback chain: requested locale -> en -> et ->
 * first one present -> "". A plain string is returned unchanged.
 * NOTE: English comes BEFORE Estonian. When an extra locale (fi/de/...) has no
 * translation the English text is shown, not the Estonian one - `et` is only
 * the last resort.
 */
export function pickLang(value: LocalizedString | undefined, locale: string = DEFAULT_LOCALE): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return (
    value[locale] ??
    value.en ??
    value[DEFAULT_LOCALE] ??
    Object.values(value).find((v): v is string => typeof v === "string") ??
    ""
  );
}

export function isLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}
